import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Mail, MessageSquare, Send, MapPin, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen py-16 flex items-center justify-center" data-testid="contact-success">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
            <Send className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4 font-['Montserrat']">Message Sent!</h1>
          <p className="text-white/60 mb-8">
            Thank you for reaching out. We'll get back to you as soon as possible.
          </p>
          <Button 
            onClick={() => {
              setSubmitted(false);
              setForm({ name: "", email: "", subject: "", message: "" });
            }}
            className="gradient-neon text-white rounded-full"
          >
            Send Another Message
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Contact Us – PlaySpiro</title>
        <meta name="description" content="Get in touch with the PlaySpiro team for casino complaints, partnership enquiries and general questions." />
        <link rel="canonical" href="https://playspiro.com/contact" />
      </Helmet>
    <div className="min-h-screen py-8" data-testid="contact-page">
      {/* Header */}
      <section className="gradient-hero py-12 mb-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4 font-['Montserrat']">
            Contact <span className="text-primary">Us</span>
          </h1>
          <p className="text-white/60 max-w-2xl mx-auto">
            Have questions, feedback, or need assistance? We'd love to hear from you.
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="space-y-6">
            <div className="glass rounded-2xl p-6">
              <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-white font-bold mb-2">Email Us</h3>
              <p className="text-white/60 text-sm mb-3">We'll respond within 24 hours</p>
              <div className="space-y-2">
                <a href="mailto:support@playspiro.com" className="block text-primary hover:underline">
                  support@playspiro.com
                </a>
                <a href="mailto:partner@playspiro.com" className="block text-secondary hover:underline">
                  partner@playspiro.com
                </a>
              </div>
            </div>

            <div className="glass rounded-2xl p-6">
              <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-secondary" />
              </div>
              <h3 className="text-white font-bold mb-2">Support Hours</h3>
              <p className="text-white/60 text-sm mb-3">We're here to help</p>
              <p className="text-white/70">Monday - Friday</p>
              <p className="text-white/70">9:00 AM - 6:00 PM (UTC)</p>
            </div>

            <div className="glass rounded-2xl p-6">
              <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center mb-4">
                <MessageSquare className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="text-white font-bold mb-2">Quick Response</h3>
              <p className="text-white/60 text-sm">
                For urgent matters, we typically respond within a few hours during business hours.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="glass rounded-2xl p-6 md:p-8">
              <h2 className="text-2xl font-bold text-white mb-6 font-['Montserrat']">Send us a Message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Your Name *</label>
                    <Input
                      placeholder="John Doe"
                      value={form.name}
                      onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                      className="bg-white/5 border-white/10 text-white h-12"
                      data-testid="contact-name-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Your Email *</label>
                    <Input
                      type="email"
                      placeholder="john@example.com"
                      value={form.email}
                      onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                      className="bg-white/5 border-white/10 text-white h-12"
                      data-testid="contact-email-input"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white/70 text-sm mb-2">Subject *</label>
                  <Input
                    placeholder="How can we help?"
                    value={form.subject}
                    onChange={(e) => setForm(prev => ({ ...prev, subject: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white h-12"
                    data-testid="contact-subject-input"
                    required
                  />
                </div>

                <div>
                  <label className="block text-white/70 text-sm mb-2">Message *</label>
                  <Textarea
                    placeholder="Your message..."
                    value={form.message}
                    onChange={(e) => setForm(prev => ({ ...prev, message: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white min-h-[180px]"
                    data-testid="contact-message-input"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-glow gradient-neon text-white font-bold rounded-full h-14 text-lg"
                  data-testid="contact-submit-btn"
                >
                  {loading ? (
                    <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" /> Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
      </>
  );
}